import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useT } from '../i18n/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../data/DataContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Tabs from '../components/ui/Tabs';
import DataTable from '../components/ui/DataTable';
import EmptyState from '../components/ui/EmptyState';
import ProgressBar from '../components/ui/ProgressBar';
import SearchRow, { SearchInput } from '../components/ui/SearchRow';
import Select from '../components/ui/Select';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import BusFormModal from '../components/transport/BusFormModal';
import LocationFormModal from '../components/transport/LocationFormModal';
import AssignmentFormModal from '../components/transport/AssignmentFormModal';
import { useApi } from '../api/client';
import * as transportApi from '../api/transport';
import useDebounce from '../hooks/useDebounce';
import { formatDate } from '../utils/format';

// Default to the next upcoming pilgrimage — that's what coordinators plan for.
function defaultEventId(events) {
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events
    .filter((e) => e.date >= today && e.status !== 'cancelled')
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  return (upcoming[0] || events[0])?.id || '';
}

export default function TransportPage() {
  const t = useT();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const { events } = useData();
  const canManage = hasPermission('transport:manage');

  const [tab, setTab] = useState('buses');
  const [buses, setBuses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 200);
  const [eventId, setEventId] = useState('');

  const [busForm, setBusForm] = useState({ open: false, editing: null });
  const [locationForm, setLocationForm] = useState({ open: false, editing: null });
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleting, setDeleting] = useState(null); // { kind, item }

  const load = useCallback(async () => {
    if (!useApi) return;
    setLoading(true);
    try {
      const [b, l] = await Promise.all([transportApi.listBuses(), transportApi.listLocations()]);
      setBuses(b || []);
      setLocations(l || []);
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  const loadAssignments = useCallback(async () => {
    if (!useApi || !eventId) {
      setAssignments([]);
      return;
    }
    setLoadingAssignments(true);
    try {
      setAssignments((await transportApi.listAssignments(eventId)) || []);
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setLoadingAssignments(false);
    }
  }, [eventId, toast, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAssignments(); }, [loadAssignments]);
  useEffect(() => {
    if (!eventId && events.length) setEventId(defaultEventId(events));
  }, [events, eventId]);

  const filteredBuses = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return buses;
    return buses.filter((b) =>
      [b.plateNumber, b.name, b.driverName, b.driverPhone].filter(Boolean).join(' ').toLowerCase().includes(q)
    );
  }, [buses, debouncedQuery]);

  const filteredLocations = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter((l) => [l.name, l.address].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [locations, debouncedQuery]);

  // Group assignments per departure location: seats are the sum of the bus
  // capacities there, travellers come from confirmed reservations.
  const groups = useMemo(() => {
    const map = new Map();
    assignments.forEach((a) => {
      const key = a.departureLocationId;
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          name: a.departureLocationName,
          passengers: 0,
          seats: 0,
          items: [],
        });
      }
      const g = map.get(key);
      g.items.push(a);
      g.seats += Number(a.capacity) || 0;
      g.passengers = Math.max(g.passengers, Number(a.passengersAtLocation) || 0);
    });
    return [...map.values()].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [assignments]);

  const totals = useMemo(
    () => groups.reduce(
      (acc, g) => ({ seats: acc.seats + g.seats, passengers: acc.passengers + g.passengers }),
      { seats: 0, passengers: 0 }
    ),
    [groups]
  );

  const assignedBusIds = useMemo(() => new Set(assignments.map((a) => a.busId)), [assignments]);

  const eventOptions = useMemo(
    () => [...events]
      .sort((a, b) => String(b.date).localeCompare(String(a.date)))
      .map((e) => ({ value: e.id, label: `${e.name} · ${formatDate(e.date, t)}` })),
    [events, t]
  );

  if (!useApi) {
    return (
      <Card title={t('pages.transport.title')} subtitle={t('pages.transport.subtitle')}>
        <EmptyState icon="🔌" title={t('common.api_required')} />
      </Card>
    );
  }

  const selectedEvent = events.find((e) => e.id === eventId);

  const handleBusSubmit = async (data) => {
    try {
      if (busForm.editing) {
        const saved = await transportApi.updateBus(busForm.editing.id, data);
        setBuses((prev) => prev.map((b) => (b.id === saved.id ? saved : b)));
        toast.success(t('transport_page.toast_bus_saved', { plate: saved.plateNumber }));
      } else {
        const saved = await transportApi.createBus(data);
        setBuses((prev) => [saved, ...prev]);
        toast.success(t('transport_page.toast_bus_saved', { plate: saved.plateNumber }));
      }
      setBusForm({ open: false, editing: null });
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    }
  };

  const handleLocationSubmit = async (data) => {
    try {
      if (locationForm.editing) {
        const saved = await transportApi.updateLocation(locationForm.editing.id, data);
        setLocations((prev) => prev.map((l) => (l.id === saved.id ? saved : l)));
        toast.success(t('transport_page.toast_location_saved', { name: saved.name }));
      } else {
        const saved = await transportApi.createLocation(data);
        setLocations((prev) => [...prev, saved]);
        toast.success(t('transport_page.toast_location_saved', { name: saved.name }));
      }
      setLocationForm({ open: false, editing: null });
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    }
  };

  const handleAssign = async (data) => {
    try {
      await transportApi.createAssignment({ ...data, eventId });
      toast.success(t('transport_page.toast_assigned'));
      setAssignOpen(false);
      loadAssignments();
    } catch (err) {
      toast.error(err?.status === 409 ? t('transport_page.already_assigned') : (err?.message || t('toast.generic_error')));
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    const { kind, item } = deleting;
    try {
      if (kind === 'bus') {
        await transportApi.deleteBus(item.id);
        setBuses((prev) => prev.filter((b) => b.id !== item.id));
      } else if (kind === 'location') {
        await transportApi.deleteLocation(item.id);
        setLocations((prev) => prev.filter((l) => l.id !== item.id));
      } else {
        await transportApi.deleteAssignment(item.id);
        setAssignments((prev) => prev.filter((a) => a.id !== item.id));
      }
      toast.success(t('transport_page.toast_deleted'));
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setDeleting(null);
    }
  };

  const activeBadge = (active) => (
    <Badge tone={active ? 'active' : 'inactive'}>{active ? t('transport_page.active') : t('transport_page.inactive')}</Badge>
  );

  const busColumns = [
    { key: 'plate', header: t('transport_page.field_plate'), render: (r) => <span className="plate">{r.plateNumber}</span> },
    { key: 'name', header: t('transport_page.field_bus_name'), render: (r) => r.name || '—' },
    { key: 'capacity', header: t('transport_page.field_capacity'), render: (r) => t('transport_page.seats_count', { count: r.capacity }) },
    {
      key: 'driver',
      header: t('transport_page.field_driver'),
      render: (r) => (r.driverName || r.driverPhone ? (
        <div className="cell-stack">
          <span>{r.driverName || '—'}</span>
          {r.driverPhone && <span className="cell-sub">{r.driverPhone}</span>}
        </div>
      ) : '—'),
    },
    { key: 'status', header: t('common.status'), render: (r) => activeBadge(r.active) },
    { key: 'notes', header: t('common.notes'), render: (r) => r.notes || '—' },
    ...(canManage ? [{
      key: 'actions',
      header: t('common.actions'),
      render: (r) => (
        <div className="action-row">
          <button type="button" className="action-btn" onClick={() => setBusForm({ open: true, editing: r })}>{t('common.edit')}</button>
          <button type="button" className="action-btn danger" onClick={() => setDeleting({ kind: 'bus', item: r })}>{t('common.delete')}</button>
        </div>
      ),
    }] : []),
  ];

  const locationColumns = [
    { key: 'name', header: t('transport_page.field_location_name'), render: (r) => <strong>{r.name}</strong> },
    { key: 'address', header: t('transport_page.field_address'), render: (r) => r.address || '—' },
    { key: 'time', header: t('transport_page.field_departure_time'), cellClassName: 'mono', render: (r) => r.departureTime || '—' },
    { key: 'status', header: t('common.status'), render: (r) => activeBadge(r.active) },
    ...(canManage ? [{
      key: 'actions',
      header: t('common.actions'),
      render: (r) => (
        <div className="action-row">
          <button type="button" className="action-btn" onClick={() => setLocationForm({ open: true, editing: r })}>{t('common.edit')}</button>
          <button type="button" className="action-btn danger" onClick={() => setDeleting({ kind: 'location', item: r })}>{t('common.delete')}</button>
        </div>
      ),
    }] : []),
  ];

  const tabs = [
    { value: 'buses', label: t('transport_page.tab_buses'), icon: '🚌', count: buses.length },
    { value: 'locations', label: t('transport_page.tab_locations'), icon: '📍', count: locations.length },
    { value: 'assignments', label: t('transport_page.tab_assignments'), icon: '🗺️' },
  ];

  const deleteCopy = deleting
    ? {
      bus: t('transport_page.delete_bus_body', { plate: deleting.item.plateNumber }),
      location: t('transport_page.delete_location_body', { name: deleting.item.name }),
      assignment: t('transport_page.delete_assignment_body', { plate: deleting.item.plateNumber }),
    }[deleting.kind]
    : '';

  return (
    <div>
      <Tabs tabs={tabs} value={tab} onChange={setTab} />

      {tab === 'buses' && (
        <>
          <SearchRow>
            <SearchInput value={query} onChange={setQuery} placeholder={t('transport_page.search_buses')} />
            {canManage && (
              <Button variant="gold" onClick={() => setBusForm({ open: true, editing: null })}>+ {t('transport_page.new_bus')}</Button>
            )}
          </SearchRow>
          <Card title={t('transport_page.buses_title')} subtitle={t('transport_page.buses_subtitle')} padded={false}>
            <DataTable
              columns={busColumns}
              rows={filteredBuses}
              empty={<EmptyState icon="🚌" title={loading ? t('common.loading') : t('transport_page.buses_empty')} />}
            />
          </Card>
        </>
      )}

      {tab === 'locations' && (
        <>
          <SearchRow>
            <SearchInput value={query} onChange={setQuery} placeholder={t('transport_page.search_locations')} />
            {canManage && (
              <Button variant="gold" onClick={() => setLocationForm({ open: true, editing: null })}>+ {t('transport_page.new_location')}</Button>
            )}
          </SearchRow>
          <Card title={t('transport_page.locations_title')} subtitle={t('transport_page.locations_subtitle')} padded={false}>
            <DataTable
              columns={locationColumns}
              rows={filteredLocations}
              empty={<EmptyState icon="📍" title={loading ? t('common.loading') : t('transport_page.locations_empty')} />}
            />
          </Card>
        </>
      )}

      {tab === 'assignments' && (
        <>
          <SearchRow>
            <Select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              options={eventOptions}
              placeholder={t('transport_page.pick_event')}
              className="filter-select filter-select--grow"
            />
            {canManage && (
              <Button variant="gold" onClick={() => setAssignOpen(true)} disabled={!eventId}>+ {t('transport_page.assign_bus')}</Button>
            )}
          </SearchRow>

          {!eventId ? (
            <Card><EmptyState icon="🗺️" title={t('transport_page.pick_event')} /></Card>
          ) : (
            <>
              <div className="transport-summary">
                <div>
                  <div className="detail-item-label">{t('transport_page.total_seats')}</div>
                  <div className="transport-summary__value">{totals.seats}</div>
                </div>
                <div>
                  <div className="detail-item-label">{t('transport_page.total_travellers')}</div>
                  <div className="transport-summary__value">{totals.passengers}</div>
                </div>
                <div>
                  <div className="detail-item-label">{t('transport_page.buses_assigned')}</div>
                  <div className="transport-summary__value">{assignments.length}</div>
                </div>
                <div className="transport-summary__note">{t('transport_page.travellers_hint')}</div>
              </div>

              {groups.length === 0 ? (
                <Card>
                  <EmptyState
                    icon="🚌"
                    title={loadingAssignments ? t('common.loading') : t('transport_page.assignments_empty')}
                    body={loadingAssignments ? null : t('transport_page.assignments_empty_body', { name: selectedEvent?.name || '' })}
                  />
                </Card>
              ) : (
                <div className="location-groups">
                  {groups.map((g) => {
                    const missing = g.passengers - g.seats;
                    return (
                      <Card
                        key={g.id}
                        className="location-group"
                        title={`📍 ${g.name}`}
                        subtitle={t('transport_page.group_subtitle', { buses: g.items.length })}
                        actions={missing > 0
                          ? <Badge tone="completed">{t('transport_page.seats_missing', { count: missing })}</Badge>
                          : <Badge tone="active">{t('transport_page.seats_ok', { count: g.seats - g.passengers })}</Badge>}
                      >
                        <div className="location-group__capacity">
                          <span>{t('transport_page.capacity_line', { passengers: g.passengers, seats: g.seats })}</span>
                          <ProgressBar value={Math.min(g.passengers, g.seats)} max={Math.max(1, g.seats)} />
                        </div>
                        <ul className="bus-list">
                          {g.items.map((a) => (
                            <li key={a.id} className="bus-list__item">
                              <span className="plate">{a.plateNumber}</span>
                              <div className="bus-list__info">
                                <div>
                                  <strong>{a.busName || t('transport_page.bus')}</strong>
                                  {' · '}{t('transport_page.seats_count', { count: a.capacity })}
                                  {a.departureTime && <> · 🕒 {a.departureTime}</>}
                                </div>
                                <div className="cell-sub">
                                  {[a.driverName, a.driverPhone].filter(Boolean).join(' · ') || t('transport_page.no_driver')}
                                  {a.notes && <> — {a.notes}</>}
                                </div>
                              </div>
                              {canManage && (
                                <button type="button" className="action-btn danger" onClick={() => setDeleting({ kind: 'assignment', item: a })}>
                                  {t('transport_page.unassign')}
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}

      <BusFormModal
        isOpen={busForm.open}
        onClose={() => setBusForm({ open: false, editing: null })}
        onSubmit={handleBusSubmit}
        initial={busForm.editing}
      />

      <LocationFormModal
        isOpen={locationForm.open}
        onClose={() => setLocationForm({ open: false, editing: null })}
        onSubmit={handleLocationSubmit}
        initial={locationForm.editing}
      />

      <AssignmentFormModal
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        onSubmit={handleAssign}
        eventName={selectedEvent?.name}
        buses={buses}
        locations={locations}
        assignedBusIds={assignedBusIds}
      />

      <ConfirmDialog
        isOpen={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting?.kind === 'assignment' ? t('transport_page.unassign_title') : t('transport_page.delete_title')}
        body={deleteCopy}
        confirmLabel={deleting?.kind === 'assignment' ? t('transport_page.unassign') : t('common.delete')}
      />
    </div>
  );
}
